import { groupDetailOptions } from '@doneverse/data/queries'
import { groupService } from '@doneverse/data/services'
import type { CreateGroupInput, GroupVisibility } from '@doneverse/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Plus, Save, Settings, Trash2, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

/**
 * "New group" — opens from the groups sidebar (admins only). On success it
 * jumps straight into the new group so it behaves like the rest of the view.
 */
export function CreateGroupDialog() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<GroupVisibility>('public')
  const [allowMemberPosts, setAllowMemberPosts] = useState(true)

  const reset = () => {
    setName('')
    setSlug('')
    setDescription('')
    setVisibility('public')
    setAllowMemberPosts(true)
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateGroupInput) => groupService.createGroup(input),
    onSuccess: (group) => {
      toast.success('Group created')
      setOpen(false)
      reset()
      void queryClient.invalidateQueries({ queryKey: ['groups'] })
      void navigate({ to: '/groups/$groupId', params: { groupId: group.id } })
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to create group'),
  })

  const slugValid = !slug || SLUG_RE.test(slug)
  const canSubmit = !!name.trim() && !!slug && slugValid && !createMutation.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    createMutation.mutate({
      name: name.trim(),
      slug,
      description: description.trim() || undefined,
      visibility,
      allow_member_posts: allowMemberPosts,
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) reset()
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full justify-start gap-2" aria-label="New group">
          <Plus className="h-4 w-4" />
          New group
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create group</DialogTitle>
          <DialogDescription>Add a new community group.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="group-name">Name</Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hustler Community"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="group-slug">Slug</Label>
            <Input
              id="group-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="e.g. hustler-community"
              required
              aria-invalid={!slugValid}
              aria-describedby={slugValid ? undefined : 'group-slug-error'}
            />
            {!slugValid ? (
              <p id="group-slug-error" className="text-xs text-destructive">
                Slug must be lowercase alphanumeric with hyphens.
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="group-description">Description</Label>
            <Textarea
              id="group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short one-liner shown under the group name"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="group-visibility">Visibility</Label>
            <Select value={visibility} onValueChange={(v) => setVisibility(v as GroupVisibility)}>
              <SelectTrigger id="group-visibility" aria-label="Visibility">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public (anyone can join)</SelectItem>
                <SelectItem value="private">Private (invite-only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-md border border-surface-border px-3 py-2">
            <Label htmlFor="group-allow-member-posts">Members can post</Label>
            <Switch
              id="group-allow-member-posts"
              checked={allowMemberPosts}
              onCheckedChange={setAllowMemberPosts}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {createMutation.isPending ? 'Creating…' : 'Create group'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * "Manage" gear in the group top bar (owners / moderators / admins). Edits
 * metadata + archives the group — the same controls the old standalone admin
 * page had, folded into the shared view.
 */
export function ManageGroupDialog({ groupId }: { groupId: string }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const detail = useQuery(groupDetailOptions(groupId))
  const d = detail.data

  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<GroupVisibility>('public')
  const [allowMemberPosts, setAllowMemberPosts] = useState(true)

  // Re-hydrate the form whenever the dialog opens or the group changes.
  useEffect(() => {
    if (!d) return
    setName(d.name)
    setDescription(d.description ?? '')
    setVisibility(d.visibility)
    setAllowMemberPosts(d.allow_member_posts)
  }, [d, open])

  const updateMutation = useMutation({
    mutationFn: () =>
      groupService.updateGroup(groupId, {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        visibility,
        allow_member_posts: allowMemberPosts,
      }),
    onSuccess: () => {
      toast.success('Group updated')
      setOpen(false)
      void queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to update'),
  })

  const archiveMutation = useMutation({
    mutationFn: () => groupService.archiveGroup(groupId),
    onSuccess: () => {
      toast.success('Group archived')
      setOpen(false)
      void queryClient.invalidateQueries({ queryKey: ['groups'] })
      void navigate({ to: '/groups' })
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to archive group'),
  })

  const dirty =
    !!d &&
    (name !== d.name ||
      (description.trim() || null) !== (d.description ?? null) ||
      visibility !== d.visibility ||
      allowMemberPosts !== d.allow_member_posts)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings className="h-4 w-4" />
          Manage
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage group</DialogTitle>
          <DialogDescription>
            Edit metadata, visibility, and posting. Members and posts are managed in the group itself.
          </DialogDescription>
        </DialogHeader>

        {detail.isLoading || !d ? (
          <div className="space-y-3 pt-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-10 w-40" />
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="manage-group-name">Name</Label>
              <Input id="manage-group-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manage-group-description">Description</Label>
              <Textarea
                id="manage-group-description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manage-group-visibility">Visibility</Label>
              <Select value={visibility} onValueChange={(v) => setVisibility(v as GroupVisibility)}>
                <SelectTrigger id="manage-group-visibility" aria-label="Visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-md border border-surface-border px-3 py-2">
              <Label htmlFor="manage-group-allow-member-posts">Members can post</Label>
              <Switch
                id="manage-group-allow-member-posts"
                checked={allowMemberPosts}
                onCheckedChange={setAllowMemberPosts}
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => updateMutation.mutate()}
                disabled={!dirty || !name.trim() || updateMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {updateMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>

            <div className="flex items-center justify-between gap-4 rounded-md border border-destructive/40 px-3 py-3">
              <div className="flex items-center gap-2 text-sm text-destructive">
                <TriangleAlert className="h-4 w-4 shrink-0" />
                <span>Archiving hides the group and removes member access.</span>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Archive
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive {d.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Members lose access immediately. This is a soft-archive — it can be reversed in the
                      database, but there is no UI for it.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => archiveMutation.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
